import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { IMAGES } from '../lib/constants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: '',
  email: '',
  phone: '',
  address: '',
  project_details: '',
  budget_range: '',
  timeline: '',
  pool_type: '',
};

export const QuoteForm = () => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error('Please fill in your name, email, and phone number.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success('Quote request submitted! We will be in touch within one business day.');
      setForm(initialForm);
    } catch (err) {
      toast.error('Something went wrong. Please try again or call us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="relative py-24 md:py-32 overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src={IMAGES.gallery[1].src}
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0F1D32]/85" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left text */}
          <div>
            <p className="text-amber-400 font-body text-xs font-semibold uppercase tracking-[0.25em] mb-4">
              Get Started
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-700 text-white leading-tight italic">
              Request a Quote
            </h2>
            <p className="mt-6 text-base text-white/50 font-body leading-relaxed max-w-md">
              Tell us about your project. We respond within one business
              day with a clear next step — no pressure, no obligation.
            </p>
            <div className="mt-10 space-y-5">
              {[
                'Clear, detailed proposals',
                'Transparent pricing — no hidden fees',
                'Organized process from day one',
              ].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                  <p className="text-sm text-white/70 font-body">{item}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Form card */}
          <form
            onSubmit={handleSubmit}
            data-testid="quote-form"
            className="bg-white rounded-sm p-8 md:p-10 shadow-2xl"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <Label htmlFor="name" className="font-body text-xs text-slate-500 uppercase tracking-wider">Full Name *</Label>
                <Input
                  id="name"
                  data-testid="quote-name-input"
                  placeholder="Your full name"
                  value={form.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="rounded-sm border-slate-200 focus:border-amber-500 focus:ring-amber-500"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email" className="font-body text-xs text-slate-500 uppercase tracking-wider">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="quote-email-input"
                  placeholder="you@email.com"
                  value={form.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="rounded-sm border-slate-200 focus:border-amber-500 focus:ring-amber-500"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="phone" className="font-body text-xs text-slate-500 uppercase tracking-wider">Phone *</Label>
                <Input
                  id="phone"
                  type="tel"
                  data-testid="quote-phone-input"
                  placeholder="(000) 000-0000"
                  value={form.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="rounded-sm border-slate-200 focus:border-amber-500 focus:ring-amber-500"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="address" className="font-body text-xs text-slate-500 uppercase tracking-wider">Project Address</Label>
                <Input
                  id="address"
                  data-testid="quote-address-input"
                  placeholder="Property address"
                  value={form.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  className="rounded-sm border-slate-200 focus:border-amber-500 focus:ring-amber-500"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="font-body text-xs text-slate-500 uppercase tracking-wider">Pool Type</Label>
                <Select value={form.pool_type} onValueChange={(val) => handleChange('pool_type', val)}>
                  <SelectTrigger data-testid="quote-pooltype-select" className="rounded-sm border-slate-200">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in-ground-pool">In-Ground Pool</SelectItem>
                    <SelectItem value="pool-and-spa">Pool & Spa</SelectItem>
                    <SelectItem value="spa-hot-tub">Spa / Hot Tub</SelectItem>
                    <SelectItem value="pool-renovation">Pool Renovation</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="font-body text-xs text-slate-500 uppercase tracking-wider">Budget Range</Label>
                <Select value={form.budget_range} onValueChange={(val) => handleChange('budget_range', val)}>
                  <SelectTrigger data-testid="quote-budget-select" className="rounded-sm border-slate-200">
                    <SelectValue placeholder="Select budget" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="under-30k">Under $30,000</SelectItem>
                    <SelectItem value="30k-50k">$30,000 - $50,000</SelectItem>
                    <SelectItem value="50k-75k">$50,000 - $75,000</SelectItem>
                    <SelectItem value="75k-100k">$75,000 - $100,000</SelectItem>
                    <SelectItem value="100k-plus">$100,000+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5 mt-5">
              <Label className="font-body text-xs text-slate-500 uppercase tracking-wider">Timeline</Label>
              <Select value={form.timeline} onValueChange={(val) => handleChange('timeline', val)}>
                <SelectTrigger data-testid="quote-timeline-select" className="rounded-sm border-slate-200">
                  <SelectValue placeholder="When are you looking to start?" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="asap">As soon as possible</SelectItem>
                  <SelectItem value="1-3-months">1 - 3 months</SelectItem>
                  <SelectItem value="3-6-months">3 - 6 months</SelectItem>
                  <SelectItem value="6-12-months">6 - 12 months</SelectItem>
                  <SelectItem value="flexible">Flexible</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5 mt-5">
              <Label htmlFor="details" className="font-body text-xs text-slate-500 uppercase tracking-wider">Project Details</Label>
              <Textarea
                id="details"
                data-testid="quote-details-textarea"
                placeholder="Tell us about your project — size, features, goals, or anything helpful."
                value={form.project_details}
                onChange={(e) => handleChange('project_details', e.target.value)}
                className="rounded-sm border-slate-200 focus:border-amber-500 focus:ring-amber-500 min-h-[100px]"
              />
            </div>

            <Button
              type="submit"
              size="lg"
              data-testid="quote-submit-btn"
              disabled={loading}
              className="w-full mt-6 bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm uppercase tracking-wider py-6 rounded-sm"
            >
              {loading ? 'Submitting...' : 'Submit Quote Request'}
              {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};
